import { type ApiRequestService } from '@coris-ts/repository/data-source/api-request.service'
import { Cached } from '@coris-ts/helpers/utilities'
import { GetRepositoryMapper } from '@coris-ts/repository/repository-mapper'
import {
  provideDefaultArrayNetworkDataSource,
  provideDefaultNetworkDataSource
} from '@coris-ts/repository/data-source/network.data-source'
import { SingleGetDataSourceRepository } from '@coris-ts/repository/single-data-source.repository'
import { GetInteractor } from '@coris-ts/repository/interactor/get.interactor'
import { MRRItemEntity } from './data/entities/mrr.entity'
import { CashFlowItemEntity } from './data/entities/cash-flow.entity'
import { ChurnItemEntity } from './data/entities/churn.entity'
import { MRREntityToMRRResponseMapper } from './domain/mappers/mrr.mappers'
import { CashFlowEntityToCashFlowResponseMapper } from './domain/mappers/cash-flow.mappers'
import { ChurnEntityToChurnResponseMapper } from './domain/mappers/churn.mappers'
import { type MRRResponse } from './domain/models/mrr'
import { type CashFlowResponse } from './domain/models/cash-flow'
import { type ChurnResponse } from './domain/models/churn'
import { GetMRRInteractor } from './domain/interactors/get-mrr.interactor'
import { GetCashFlowInteractor } from './domain/interactors/get-cash-flow.interactor'
import { GetChurnInteractor } from './domain/interactors/get-churn.interactor'
import { GetSubscriberIntervalsInteractor } from './domain/interactors/get-subscriber-intervals.interactor'
import { SubscriberIntervalsEntity } from './data/entities/subscriber-intervals.entity'
import { type SubscriberIntervals } from './domain/models/subscriber-intervals'

export interface RevenueComponent {
  provideGetMRR: () => GetMRRInteractor
  provideGetCashFlow: () => GetCashFlowInteractor
  provideGetChurn: () => GetChurnInteractor
  provideGetSubscriberIntervals: () => GetSubscriberIntervalsInteractor
}

export class RevenueDefaultModule implements RevenueComponent {
  constructor(private readonly apiRequestService: ApiRequestService) {}

  @Cached()
  private getMRRRepository(): GetRepositoryMapper<MRRItemEntity[], MRRResponse> {
    const dataSource = provideDefaultArrayNetworkDataSource(this.apiRequestService, MRRItemEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, new MRREntityToMRRResponseMapper())
  }

  @Cached()
  private getCashFlowRepository(): GetRepositoryMapper<CashFlowItemEntity[], CashFlowResponse> {
    const dataSource = provideDefaultArrayNetworkDataSource(this.apiRequestService, CashFlowItemEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, new CashFlowEntityToCashFlowResponseMapper())
  }

  @Cached()
  private getChurnRepository(): GetRepositoryMapper<ChurnItemEntity[], ChurnResponse> {
    const dataSource = provideDefaultArrayNetworkDataSource(this.apiRequestService, ChurnItemEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, new ChurnEntityToChurnResponseMapper())
  }

  public provideGetMRR(): GetMRRInteractor {
    return new GetMRRInteractor(new GetInteractor<MRRResponse>(this.getMRRRepository()))
  }

  public provideGetCashFlow(): GetCashFlowInteractor {
    return new GetCashFlowInteractor(new GetInteractor<CashFlowResponse>(this.getCashFlowRepository()))
  }

  public provideGetChurn(): GetChurnInteractor {
    return new GetChurnInteractor(new GetInteractor<ChurnResponse>(this.getChurnRepository()))
  }

  @Cached()
  private getSubscriberIntervalsRepository(): GetRepositoryMapper<SubscriberIntervalsEntity, SubscriberIntervals> {
    const dataSource = provideDefaultNetworkDataSource(this.apiRequestService, SubscriberIntervalsEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: SubscriberIntervalsEntity) => entity })
  }

  public provideGetSubscriberIntervals(): GetSubscriberIntervalsInteractor {
    return new GetSubscriberIntervalsInteractor(
      new GetInteractor<SubscriberIntervals>(this.getSubscriberIntervalsRepository())
    )
  }
}
